import { MediaContentTypes } from "constants/contentTypes"
import { RenderReactAtSite } from "widgets/commonComponents/inline/renderReactAtSite"
import { SiteMedia } from "widgets/siteWidgets/siteMedia"
import { SiteImageData, SiteVideoData } from "widgets/interfaces"
import { SiteLayoutType } from "pagesPage/stateInterfaces"
import { TimelineItemData, TimelineItemMediaProportion } from "articlePage/timelineArticle/timelineSpecialData"
import { ArticleImageGallery } from "widgets/defenitions/article/componentas/articleImageGalleryComponenta/articleImageGalleryComponenta"
import ArticleImageSliderGalleryComponenta from "widgets/defenitions/article/componentas/articleImageGalleryComponenta/articleImageSliderGalleryComponenta"
import { TimelineResolvedImage, TimelineResolvedVideo } from "./timelineFrontData"

interface TimelineItemMediaProps {
    item: TimelineItemData
    articleId: string
    images: { [id: string]: TimelineResolvedImage }
    videos: { [id: string]: TimelineResolvedVideo }
    isMobileWeb?: boolean
    isApp?: boolean
}

const DESKTOP_MEDIA_SIZES: Record<TimelineItemMediaProportion, { width: number, height: number }> = {
    "16:9": { width: 660, height: 371 },
    "9:16": { width: 309, height: 548 },
}

const APP_MEDIA_SIZES: Record<TimelineItemMediaProportion, { width: number, height: number }> = {
    "16:9": { width: 360, height: 200 },
    "9:16": { width: 360, height: 540 },
}

const MOBILE_WEB_MEDIA_SIZES: Record<TimelineItemMediaProportion, { width: number, height: number }> = {
    "16:9": { width: 591, height: 328 },
    "9:16": { width: 591, height: 886 },
}

const DEFAULT_VIDEO_SETTINGS = { isVideoLightbox: true, hideVideoAds: false }

const proportionClass = (proportion: TimelineItemMediaProportion) =>
    proportion === "9:16" ? "proportion-9-16" : "proportion-16-9"

export class TimelineItemMedia extends React.Component<TimelineItemMediaProps> {
    private getSizes = () => {
        const { item, isMobileWeb, isApp } = this.props
        const proportion = item.mediaProportion || "16:9"
        if (isApp) {
            return APP_MEDIA_SIZES[proportion]
        }
        if (isMobileWeb) {
            return MOBILE_WEB_MEDIA_SIZES[proportion]
        }
        return DESKTOP_MEDIA_SIZES[proportion]
    }

    private renderImage = (imageId: string) => {
        const { images, item } = this.props
        const image = images && images[imageId]
        if (!image || !image.url) {
            return null
        }

        const { width, height } = this.getSizes()
        const media: SiteImageData = {
            mediaType: MediaContentTypes.MEDIA_IMAGE,
            url: image.url,
            alt: image.alt || item.title || "",
            credit: image.credit || "",
            title: image.caption || item.title || "",
            className: "",
            mediaId: imageId,
        }

        return (
            <div className={`timeline-new-media-image ${proportionClass(item.mediaProportion || "16:9")}`}>
                <SiteMedia width={width} height={height} data={media} videoSettings={DEFAULT_VIDEO_SETTINGS} isImageLinkable={false} />
                {(image.credit || image.caption) &&
                    <div className="timeline-new-media-caption">
                        {image.caption && <span className="timeline-new-media-caption-text">{image.caption}</span>}
                        {image.credit &&
                            <span className="timeline-new-media-credit">(צילום: <span>{image.credit}</span>)</span>
                        }
                    </div>
                }
            </div>
        )
    }

    private renderVideo = () => {
        const { item, videos } = this.props
        const videoId = item.mediaIds[0]
        const video = videoId && videos ? videos[videoId] : null
        if (!video || !video.main_link) {
            return null
        }

        const { width, height } = this.getSizes()
        const media: SiteVideoData = {
            mediaType: MediaContentTypes.MEDIA_VIDEO,
            url: video.main_link || "",
            downGradeUrl: video.is_drm ? "" : (video.low_res_link || ""),
            poster: video.posterSrc || "",
            title: video.videoName || item.title || "",
            youtube_id: video.youtube_id || "",
            mediaId: video.mediaVideoId || videoId,
            blockAbroadViews: !!video.blockAbroadViews,
            duration: video.duration,
            is_drm: !!video.is_drm,
            credit: video.credit || "",
            isPlayerBarVisible: true,
            isMuteAvailable: true,
            isSoundButtonVisible: true,
            isAutoPlaying: false,
            isLoopPlaying: false,
        }

        return (
            <div className={`timeline-new-media-video ${proportionClass(item.mediaProportion || "16:9")}`}>
                <SiteMedia width={width} height={height} data={media} videoSettings={DEFAULT_VIDEO_SETTINGS} isImageLinkable={false} />
                {video.credit &&
                    <div className="timeline-new-media-caption">
                        <span className="timeline-new-media-credit">(צילום: <span>{video.credit}</span>)</span>
                    </div>
                }
            </div>
        )
    }

    private renderGallery = () => {
        const { item, articleId, images, isMobileWeb, isApp } = this.props
        const galleryImages: ArticleImageGallery[] = (item.mediaIds || []).filter(Boolean).map(imageId => {
            const image = images && images[imageId]
            if (!image || !image.url) {
                return null
            }
            return {
                imageId,
                imageSrc: image.url,
                componentaId: item.id,
                credit: image.credit || "",
                caption: image.caption || "",
            }
        }).filter(Boolean)

        if (!galleryImages.length) {
            return null
        }

        const siteLayoutType: SiteLayoutType = isApp ? "mobileApp" : isMobileWeb ? "mobileWeb" : "desktop"
        const mainOptions = {
            type: "slide",
            perPage: 1,
            perMove: 1,
            pagination: false,
            cover: true,
            direction: "rtl",
            heightRatio: 0.5625,
            rewind: false,
            drag: false,
        }
        const desktopThumbs = {
            type: "slide",
            gap: "9.25px",
            pagination: false,
            fixedWidth: 178,
            height: 104,
            cover: true,
            direction: "rtl",
            isNavigation: true,
            arrows: false,
            perPage: 4,
            focus: "center",
            drag: true,
        }
        const thumbOptions = isApp
            ? { ...desktopThumbs, gap: "2.5vw", width: "100%", height: "14.063vw", fixedWidth: 0, drag: false, focus: 3, arrows: false }
            : isMobileWeb
                ? { ...desktopThumbs, gap: "2.5vw", width: "100%", height: "14.063vw", fixedWidth: 0 }
                : desktopThumbs

        return (
            <div className="timeline-new-media-gallery">
                <RenderReactAtSite>
                    <ArticleImageSliderGalleryComponenta
                        isLTR={false}
                        articleId={articleId}
                        componentaId={item.id}
                        images={galleryImages}
                        mainOptions={mainOptions}
                        thumbOptions={thumbOptions}
                        siteLayoutType={siteLayoutType}
                        imagesJson={isApp ? JSON.stringify(galleryImages) : undefined}
                    />
                </RenderReactAtSite>
            </div>
        )
    }

    public render() {
        const { item } = this.props

        if (item.mediaType === "image" && item.mediaIds[0]) {
            return this.renderImage(item.mediaIds[0])
        }

        if (item.mediaType === "video") {
            return this.renderVideo()
        }

        if (item.mediaType === "gallery") {
            return this.renderGallery()
        }

        return null
    }
}
